import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { X, Wand2, Check, AlertCircle, Search, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Normalize a string for fuzzy matching:
 * - lowercase
 * - strip extension
 * - replace separators (_,-,spaces, dots) with single space
 * - collapse spaces
 */
const normalize = (s = '') =>
  s.toLowerCase()
    .replace(/\.[^/.]+$/, '') // strip extension
    .replace(/[_\-.\s]+/g, ' ')
    .replace(/[^a-z0-9 ]/g, '')
    .trim();

/**
 * Score how well a media file matches a product. Higher = better.
 * Returns 0 if no match.
 */
const matchScore = (productName, mediaName) => {
  const p = normalize(productName);
  const m = normalize(mediaName);
  if (!p || !m) return 0;
  // Exact normalized match
  if (p === m) return 100;
  // Media filename starts with product name
  if (m.startsWith(p)) return 90;
  // Product name contains media name (e.g. media "MEBENDA" for product "MEBENDA PLUS")
  if (p.startsWith(m)) return 85;
  // Token overlap
  const pTokens = new Set(p.split(' ').filter(t => t.length > 2));
  const mTokens = new Set(m.split(' ').filter(t => t.length > 2));
  if (pTokens.size === 0 || mTokens.size === 0) return 0;
  let common = 0;
  pTokens.forEach(t => { if (mTokens.has(t)) common += 1; });
  if (common === 0) return 0;
  return Math.round((common / Math.max(pTokens.size, mTokens.size)) * 70);
};

const BulkImageAttachDialog = ({ open, onClose, user, products, onApplied }) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [search, setSearch] = useState('');
  // matches: { [product_name]: { libraryId, mediaName, score, picked: bool } }
  const [matches, setMatches] = useState({});

  // Products without images (eligible for bulk attach)
  const eligibleProducts = useMemo(
    () => products.filter(p => !p.product_image),
    [products]
  );

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/media/files`, { headers: { Authorization: `Bearer ${user.token}` } })
      .then(r => {
        const items = r.data?.files || r.data || [];
        setMediaItems(items);
        // Auto-match products to media
        const map = {};
        eligibleProducts.forEach(prod => {
          let best = null;
          items.forEach(media => {
            const score = matchScore(prod.product_name, media.original_name || media.filename);
            if (score > 0 && (!best || score > best.score)) {
              best = { libraryId: media.id, mediaName: media.original_name, score, url: media.url };
            }
          });
          if (best) {
            map[prod.product_name] = { ...best, picked: best.score >= 70 };
          }
        });
        setMatches(map);
      })
      .catch(() => toast.error('Failed to load media library'))
      .finally(() => setLoading(false));
  }, [open, user.token, eligibleProducts]);

  const pickedCount = Object.values(matches).filter(m => m.picked).length;

  const togglePick = (productName) => {
    setMatches(prev => ({
      ...prev,
      [productName]: { ...prev[productName], picked: !prev[productName]?.picked }
    }));
  };

  const handleApply = async () => {
    const payload = {
      mappings: Object.entries(matches)
        .filter(([_, v]) => v.picked && v.libraryId)
        .map(([product_name, v]) => ({ product_name, library_id: v.libraryId }))
    };
    if (payload.mappings.length === 0) {
      toast.warning('Pick at least one match to apply');
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(`${API}/price-list/bulk-attach-images`, payload, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const { applied, missing_products, missing_media, total_requested } = res.data;
      toast.success(`Attached ${applied} of ${total_requested} images`);
      if (missing_products?.length) {
        toast.warning(`${missing_products.length} products not found in price list`);
      }
      if (missing_media?.length) {
        toast.warning(`${missing_media.length} media files missing in storage`);
      }
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk attach failed');
    } finally {
      setApplying(false);
    }
  };

  const filteredMatches = useMemo(() => {
    const entries = Object.entries(matches);
    if (!search) return entries;
    const q = search.toLowerCase();
    return entries.filter(([prod, v]) =>
      prod.toLowerCase().includes(q) ||
      (v.mediaName || '').toLowerCase().includes(q)
    );
  }, [matches, search]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[65] flex items-center justify-center p-3 sm:p-6" data-testid="bulk-attach-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-indigo-600" />
              Bulk Attach Images
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {eligibleProducts.length} products without images • {mediaItems.length} images in Media Library
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white rounded" data-testid="bulk-attach-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 text-xs text-amber-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>
            Auto-matched by filename. Upload your images to Media Library first, then we'll pair them
            with products by name (e.g., <code className="bg-amber-100 px-1 rounded">MEBENDA.png</code> → <code className="bg-amber-100 px-1 rounded">MEBENDA PLUS</code>).
            Review and tick the matches you want, then Apply.
          </p>
        </div>

        <div className="px-5 py-2 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search products or filenames..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              data-testid="bulk-attach-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Analyzing matches...
            </div>
          ) : filteredMatches.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ImageIcon className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-medium">
                {Object.keys(matches).length === 0 ? 'No matches found' : 'No matches for your search'}
              </p>
              <p className="text-xs mt-1">
                Upload images to Media Library with filenames similar to your product names.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMatches.map(([productName, m]) => (
                <label
                  key={productName}
                  className={`flex items-center gap-3 p-2.5 border rounded-lg cursor-pointer transition-colors ${
                    m.picked ? 'border-indigo-400 bg-indigo-50/50' : 'border-slate-200 hover:bg-slate-50'
                  }`}
                  data-testid={`bulk-attach-row-${productName}`}
                >
                  <input
                    type="checkbox"
                    checked={m.picked || false}
                    onChange={() => togglePick(productName)}
                    className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                    data-testid={`bulk-attach-check-${productName}`}
                  />
                  <img
                    src={`${BACKEND_URL}${m.url}`}
                    alt={m.mediaName}
                    className="w-10 h-10 rounded object-cover border border-slate-200 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-800 truncate">{productName}</p>
                    <p className="text-xs text-slate-500 truncate">→ {m.mediaName}</p>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] rounded font-medium flex-shrink-0 ${
                    m.score >= 90 ? 'bg-emerald-100 text-emerald-700' :
                    m.score >= 70 ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {m.score >= 90 ? 'Exact' : m.score >= 70 ? 'Good' : 'Weak'} {m.score}%
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between bg-slate-50">
          <p className="text-sm text-slate-600">
            <strong className="text-indigo-700">{pickedCount}</strong> selected of {Object.keys(matches).length} matched
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm font-medium border border-slate-300 rounded-md hover:bg-white"
              disabled={applying}
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={applying || pickedCount === 0}
              data-testid="bulk-attach-apply"
              className="px-4 py-1.5 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            >
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {applying ? 'Applying...' : `Apply ${pickedCount} match${pickedCount !== 1 ? 'es' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkImageAttachDialog;
